import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "../contexts/AuthContext";
import {
  ROLE_ORG_MEMBER,
  ROLE_GRC_ADMIN,
  ROLE_AUDIT_USER,
  ROLE_RISK_ANALYST,
  ROLE_C_SUITE,
  ROLE_COSTING_USER,
  UserRoleNumber,
} from "../services/userService";
import { getPublicEntities, getAppConfig } from "../services/apiService";
import {
  Shield,
  Zap,
  CheckCircle2,
  Globe,
  Lock,
  Settings,
  AlertTriangle,
  ShieldCheck,
  Search,
  BookOpen,
  Link2,
  AlertOctagon,
  DollarSign,
  BarChart2,
  LayoutDashboard,
  Briefcase,
  Crown,
  Building2,
  TrendingUp,
  ClipboardCheck,
  Calculator,
  X,
} from "lucide-react";
import FormControl from "../components/common/FormControl";
import Toast, { ToastMessage } from "../components/common/Toast";
import "../components/auth/Login.css";

interface LoginFormData {
  login_id: string;
  password: string;
  role: UserRoleNumber | 0;
  entity_id: number | 0;
  rememberMe?: boolean;
}

interface LoginErrors {
  login_id?: string;
  password?: string;
  role?: string;
  general?: string;
  entity_id?: string;
}

interface Entity {
  id: number;
  name: string;
}

/* ── Left panel — steps, stats, pillars ───────────────── */
const STEPS = [
  { icon: <Shield size={12} />, bold: "Governance", rest: "→ Compliance" },
  {
    icon: <AlertTriangle size={12} />,
    bold: "Risk",
    rest: "→ Control Mitigation",
  },
  { icon: <ShieldCheck size={12} />, bold: "Warning", rest: "→ Assurance" },
  { icon: <BarChart2 size={12} />, bold: "Monitoring", rest: "→ Correction" },
];

const PILLARS = [
  {
    icon: <Shield size={14} />,
    title: "Governance Assurance",
    desc: "Policies aligned, ownership clear, accountability tracked.",
  },
  {
    icon: <AlertTriangle size={14} />,
    title: "Risk Anticipation",
    desc: "Emerging trends surfaced before they escalate.",
  },
  {
    icon: <CheckCircle2 size={14} />,
    title: "Control Integrity",
    desc: "Control design & execution verified in real time.",
  },
  {
    icon: <Zap size={14} />,
    title: "Rapid Alerts",
    desc: "Anomalies flagged within minutes — not weeks.",
  },
  {
    icon: <BookOpen size={14} />,
    title: "Unified Evidence",
    desc: "Single source for audits, regulators & leadership.",
  },
  {
    icon: <BarChart2 size={14} />,
    title: "Adaptive Intelligence",
    desc: "Signals learn and refine with every cycle.",
  },
];

/* ── Left-panel modules (Lucide icons) ────────────────── */
const MODULES: Array<{
  icon: React.ReactNode;
  iconBg: string;
  name: string;
  status: string;
}> = [
  {
    icon: <AlertTriangle size={20} />,
    iconBg: "rgba(248,113,113,0.18)",
    name: "Risk",
    status: "Live",
  },
  {
    icon: <ShieldCheck size={20} />,
    iconBg: "rgba(52,211,153,0.18)",
    name: "Compliance",
    status: "Ready",
  },
  {
    icon: <Search size={20} />,
    iconBg: "rgba(96,165,250,0.18)",
    name: "Audit",
    status: "Live",
  },
  {
    icon: <BookOpen size={20} />,
    iconBg: "rgba(167,139,250,0.18)",
    name: "Policy Mgmt",
    status: "Connected",
  },
  {
    icon: <Link2 size={20} />,
    iconBg: "rgba(124,58,237,0.18)",
    name: "Third-Party Risk",
    status: "Ready",
  },
  {
    icon: <AlertOctagon size={20} />,
    iconBg: "rgba(244,114,182,0.18)",
    name: "Incident",
    status: "Live",
  },
  {
    icon: <DollarSign size={20} />,
    iconBg: "rgba(74,222,128,0.18)",
    name: "Costing",
    status: "Ready",
  },
  {
    icon: <BarChart2 size={20} />,
    iconBg: "rgba(56,189,248,0.18)",
    name: "Analytics",
    status: "Connected",
  },
  {
    icon: <LayoutDashboard size={20} />,
    iconBg: "rgba(192,132,252,0.18)",
    name: "Executive Dash",
    status: "Live",
  },
];

const TRUST_ITEMS = [
  { icon: <Lock size={10} />, label: "Encrypted Auth" },
  { icon: <CheckCircle2 size={10} />, label: "SOC 2 Ready" },
  { icon: <Shield size={10} />, label: "Secure Session" },
  { icon: <Globe size={10} />, label: "99.99% Uptime" },
];

const BADGES = [
  "Single Sign-On",
  "Role-Based Access",
  "Enterprise Security",
  "Real-Time Oversight",
];

/* ── Right-panel role chips (Lucide icons) ────────────── */
const ROLE_DEFS: Array<{
  role: UserRoleNumber;
  cls: string;
  icon: React.ReactNode;
  label: string;
}> = [
  {
    role: ROLE_GRC_ADMIN,
    cls: "chip-admin",
    icon: <Briefcase size={22} />,
    label: "GRC Team",
  },
  {
    role: ROLE_C_SUITE,
    cls: "chip-csuite",
    icon: <Crown size={22} />,
    label: "Executive",
  },
  {
    role: ROLE_ORG_MEMBER,
    cls: "chip-staff",
    icon: <Building2 size={22} />,
    label: "Staff",
  },
  {
    role: ROLE_AUDIT_USER,
    cls: "chip-audit",
    icon: <ClipboardCheck size={22} />,
    label: "Audit",
  },
  {
    role: ROLE_RISK_ANALYST,
    cls: "chip-risk",
    icon: <TrendingUp size={22} />,
    label: "Analytics",
  },

  {
    role: ROLE_COSTING_USER,
    cls: "chip-costing",
    icon: <Calculator size={22} />,
    label: "Financials",
  },
];

/* ─────────────────────────────────────────────────────── */

const LoginPage: React.FC = () => {
  const { state, login, clearError } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<LoginFormData>({
    login_id: "",
    password: "",
    role: 0,
    entity_id: 0,
    rememberMe: false,
  });
  const [entities, setEntities] = useState<Entity[]>([]);
  const [errors, setErrors] = useState<LoginErrors>({});
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [comingSoonRole, setComingSoonRole] = useState<string>("");
  // Personas locked from login (not in subscription plan / under development),
  // plus the message to show. Driven by the backend /auth/app-config endpoint.
  const [lockedPersonas, setLockedPersonas] = useState<string[]>([]);
  const [lockMessage, setLockMessage] = useState<string>("");

  useEffect(() => {
    if (state.error) {
      setToast({ type: "error", message: state.error });
    }
  }, [state.error]);

  useEffect(() => {
    const loadAndRestore = async () => {
      try {
        // Load entities + public login config (which personas are locked)
        const [loadedEntities, cfg] = await Promise.all([
          getPublicEntities(),
          getAppConfig(),
        ]);
        setEntities(loadedEntities);
        setLockedPersonas(cfg.locked_personas || []);
        setLockMessage(cfg.lock_message || "");

        // Remember Me = form prefill only (session is per-tab, never restored
        // here). If set, pre-fill login id + entity; else just default the
        // entity to the last one used. User can change either.
        const wasRemem = localStorage.getItem("anygrc_remember_me") === "true";
        const savedId = localStorage.getItem("anygrc_remembered_login_id") || "";
        const savedEntityId = localStorage.getItem("anygrc_remembered_entity_id");
        const lastEntityId = localStorage.getItem("anygrc_last_entity");
        const prefEntity = (wasRemem && savedEntityId) ? savedEntityId : lastEntityId;
        const prefEntityValid = prefEntity && loadedEntities.some((e: Entity) => e.id === Number(prefEntity));

        if (wasRemem) {
          setFormData(prev => ({
            ...prev,
            login_id: savedId,
            entity_id: prefEntityValid ? Number(prefEntity) : prev.entity_id,
            rememberMe: true,
          }));
        } else if (prefEntityValid) {
          setFormData(prev => ({ ...prev, entity_id: Number(prefEntity) }));
        }
      } catch (err) {
        console.warn('Failed to load entities:', err);
      }
    };

    loadAndRestore();
  }, []);

  const clearFieldError = (name: string) => {
    if (errors[name as keyof LoginErrors])
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    clearError();
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "entity_id" ? Number(value) : value,
    }));
    clearFieldError(name);
  };

  const validateFields = (): LoginErrors => {
    const e: LoginErrors = {};
    if (!formData.entity_id) e.entity_id = "Please select an entity";
    if (!formData.login_id) e.login_id = "Login ID is required";
    if (!formData.password) e.password = "Password is required";
    else if (formData.password.length < 3) e.password = "Min 3 characters";
    return e;
  };

  const persistSession = (role: UserRoleNumber) => {
    // Per-tab isolation: the role belongs to THIS tab's session, so it lives in
    // sessionStorage (never localStorage — that would leak the role across tabs).
    sessionStorage.setItem("anygrc_user_role", String(role));

    // Remember Me = LOGIN-FORM PREFILL ONLY. It does NOT persist the session or
    // token (those stay per-tab in sessionStorage, so two tabs remain two
    // independent logins). It only remembers which login id + entity to
    // pre-fill next visit; the user can still change them. Password is
    // deliberately NOT stored (plaintext in localStorage = security risk).
    if (formData.rememberMe) {
      localStorage.setItem("anygrc_remember_me", "true");
      localStorage.setItem("anygrc_remembered_login_id", formData.login_id);
      localStorage.setItem("anygrc_remembered_entity_id", String(formData.entity_id));
    } else {
      localStorage.removeItem("anygrc_remember_me");
      localStorage.removeItem("anygrc_remembered_login_id");
      localStorage.removeItem("anygrc_remembered_entity_id");
    }
    // Scrub any legacy plaintext password left by older builds.
    localStorage.removeItem("anygrc_remembered_password");

    // Last selected entity is only a convenience prefill for the login form
    // (not part of the authenticated session), so it may stay in localStorage.
    if (formData.entity_id) {
      localStorage.setItem("anygrc_last_entity", String(formData.entity_id));
    }
  };

  // Maps the role chip to the USR_user_entity_map module-flag key the backend expects.
  const ROLE_TO_MODULE: Record<number, string> = {
    [ROLE_GRC_ADMIN]: "grc_team",
    [ROLE_C_SUITE]: "executive",
    [ROLE_ORG_MEMBER]: "compliance",
    [ROLE_AUDIT_USER]: "audit",
    [ROLE_RISK_ANALYST]: "analytics",
    [ROLE_COSTING_USER]: "financials",
  };

  const doLogin = async (role: UserRoleNumber) => {
    const success = await login(
      formData.login_id,
      formData.password,
      !!formData.rememberMe,
      String(role),
      formData.entity_id,
      ROLE_TO_MODULE[role],
    );
    if (success) {
      persistSession(role);
      setFormData({
        login_id: "",
        password: "",
        role: 0,
        entity_id: 0,
        rememberMe: false,
      });
      navigate("/dashboard");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validateFields();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    try {
      await doLogin(formData.role === 0 ? ROLE_ORG_MEMBER : formData.role);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Login failed. Please try again.";
      setToast({ type: "error", message });
    }
  };

  const handleRoleLogin = async (selectedRole: UserRoleNumber) => {
    // Locked personas (not in subscription plan / under development) — show the
    // subscription notice instead of attempting login. Backend enforces too.
    const moduleKey = ROLE_TO_MODULE[selectedRole];
    if (moduleKey && lockedPersonas.includes(moduleKey)) {
      const def = ROLE_DEFS.find((d) => d.role === selectedRole);
      setComingSoonRole(def?.label ?? "This module");
      setShowComingSoon(true);
      return;
    }

    setFormData((prev) => ({ ...prev, role: selectedRole }));
    const errs = validateFields();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    try {
      await doLogin(selectedRole);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Login failed. Please try again.";
      setToast({ type: "error", message });
    }
  };

  const handleDemoLogin = () => {
    setFormData({
      login_id: "grcadmin",
      password: "admin123",
      role: ROLE_ORG_MEMBER,
      entity_id: entities.length > 0 ? entities[0].id : 0,
      rememberMe: false,
    });
  };

  return (
    <div className="login-split-container">
      <Toast toast={toast} onClose={() => setToast(null)} />

      {/* Coming Soon Modal */}
      {showComingSoon && (
        <div
          className="login-coming-soon-overlay"
          data-testid="MOD_login_page_coming_soon_overlay"
          onClick={() => setShowComingSoon(false)}
        >
          <div
            className="login-coming-soon-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="login-coming-soon-close"
              data-testid="BTN_login_page_coming_soon_close"
              onClick={() => setShowComingSoon(false)}
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
            <div className="login-coming-soon-icon-wrap">
              <div className="login-coming-soon-icon">
                <Lock size={40} />
              </div>
            </div>
            <h3 className="login-coming-soon-title">{comingSoonRole}</h3>
            <div className="login-coming-soon-body">
              <p>
                {lockMessage ||
                  "This module is not in your subscription plan - contact your admin to take this subscription."}
              </p>
            </div>
            <div className="login-coming-soon-footer">
              <button
                className="login-coming-soon-btn"
                data-testid="BTN_login_page_coming_soon_got_it"
                onClick={() => setShowComingSoon(false)}
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════ LEFT PANEL — original oversight design ════════════ */}
      <div className="login-right-section">
        <div className="login-background-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>

        <aside
          className="oversight-panel"
          aria-label="AnyGRC value proposition"
        >
          {/* Badge */}
          <div className="oversight-badge">
            <span className="oversight-badge-dot" />
            Enterprise GRC Platform
          </div>

          <header className="oversight-header text-center">
            <h2 className="oversight-heading">Oversight Elevated</h2>
            <div className="oversight-steps-grid">
              {STEPS.map((s) => (
                <div key={s.bold} className="oversight-step-item">
                  <span className="oversight-step-icon">{s.icon}</span>
                  <span>
                    <strong>{s.bold}</strong>{" "}
                    <span className="opacity-70">{s.rest}</span>
                  </span>
                </div>
              ))}
            </div>
          </header>

          <section
            className="oversight-pillars"
            aria-label="Core capability pillars"
          >
            {PILLARS.map((p) => (
              <div key={p.title} className="pillar" aria-label={p.title}>
                <div className="pillar-icon">{p.icon}</div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </section>

          <footer
            className="oversight-footer-content"
            aria-label="Closing value message"
          >
            <img
              src={process.env.PUBLIC_URL + "/eagle.png"}
              alt="AnyGRC Eagle"
              className="eagle-footer-img"
            />
            <div className="footer-text-group">
              <h4 className="footer-heading">
                Altitude • Vision • Speed • Precision
              </h4>
              <p className="footer-quote">Attack the risk before it lands.</p>
            </div>
          </footer>
        </aside>
      </div>

      {/* ════════════ RIGHT PANEL — modern redesign ════════════ */}
      <div className="login-left-section">
        <div className="login-wrapper">
          {/* Brand */}
          <div className="login-header">
            <div className="login-logo-container">
              <img
                src={process.env.PUBLIC_URL + "/anygrc-logo.png"}
                alt="AnyGRC"
                className="login-logo-img"
              />
            </div>
            <p className="login-tagline">Oversight Elevated</p>
            <p className="login-subtitle-text">
              Governance<span className="subtitle-separator">|</span>
              Risk<span className="subtitle-separator">|</span>
              Compliance
            </p>
          </div>

          {/* Form */}
          <form className="login-form" data-testid="login_page_form" onSubmit={handleSubmit} noValidate>
            <FormControl.SearchSelect
              label="Select Entity"
              required
              name="entity_id"
              value={formData.entity_id === 0 ? null : formData.entity_id}
              onChange={(val) => {
                const num = val === null ? 0 : Number(val);
                setFormData((prev) => ({ ...prev, entity_id: num }));
                clearFieldError("entity_id");
              }}
              disabled={state.isLoading}
              error={errors.entity_id}
              placeholder="— choose entity —"
              options={entities.map((e) => ({ value: e.id, label: e.name }))}
              clearable
              data-testid="SEL_login_page_entity"
            />

            <FormControl.Input
              label="Login ID"
              required
              name="login_id"
              placeholder="Enter your login ID"
              value={formData.login_id}
              onChange={handleInputChange}
              disabled={state.isLoading}
              error={errors.login_id}
              autoComplete="username"
              data-testid="INP_login_page_username"
            />

            <FormControl.Input
              label="Password"
              required
              type="password"
              name="password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleInputChange}
              disabled={state.isLoading}
              error={errors.password}
              autoComplete="current-password"
              data-testid="INP_login_page_password"
            />

            {/* ── Role chip grid ── */}
            <div className="rp-role-section">
              <span className="rp-role-label">Sign In As</span>
              <div className="rp-role-grid">
                {/* 6 regular roles */}
                {ROLE_DEFS.map((r) => {
                  const isLocked = lockedPersonas.includes(ROLE_TO_MODULE[r.role]);
                  return (
                    <button
                      key={r.label}
                      type="button"
                      className={`rp-role-chip ${r.cls} ${formData.role === r.role ? "active" : ""} ${isLocked ? "locked" : ""}`}
                      data-testid={`BTN_login_page_role_${r.label.toLowerCase().replace(/\s+/g, "_")}`}
                      onClick={() => handleRoleLogin(r.role)}
                      disabled={state.isLoading}
                      title={isLocked ? `${r.label} — not in your subscription plan` : r.label}
                      style={isLocked ? { position: "relative", opacity: 0.55 } : undefined}
                    >
                      {isLocked && (
                        <span
                          className="rp-chip-lock"
                          style={{ position: "absolute", top: 6, right: 6, display: "flex" }}
                        >
                          <Lock size={12} />
                        </span>
                      )}
                      <span className="rp-chip-icon">{r.icon}</span>
                      <span className="rp-chip-label">{r.label}</span>
                    </button>
                  );
                })}
                {/* Super Admin — full-width chip, navigates to its own login */}
                <button
                  type="button"
                  className="rp-role-chip chip-sadmin"
                  data-testid="BTN_login_page_role_super_admin"
                  onClick={() => navigate("/super-admin/login")}
                  disabled={state.isLoading}
                  title="Super Admin login"
                >
                  <span className="rp-chip-icon">
                    <Settings size={18} />
                  </span>
                  <span className="rp-chip-label">Super Admin</span>
                </button>
              </div>
            </div>

            {/* Options row */}
            <div className="login-options-flex">
              <FormControl.Checkbox
                label="Remember me"
                checked={!!formData.rememberMe}
                onChange={(checked) =>
                  setFormData((prev) => ({ ...prev, rememberMe: checked }))
                }
                disabled={state.isLoading}
              />
              <button
                type="button"
                className="fill-defaults-btn"
                data-testid="BTN_login_page_fill_defaults"
                onClick={handleDemoLogin}
                disabled={state.isLoading}
              >
                Fill Defaults
              </button>
            </div>

            {/* CTA */}
            <button
              type="submit"
              className="lp-signin-btn"
              data-testid="BTN_login_page_submit"
              disabled={state.isLoading || !formData.entity_id}
            >
              <Zap size={15} />
              Sign In &amp; Launch Applications
            </button>
          </form>

          {/* Footer */}
          {/* <div className="login-footer">
                        <span className="security-notice">
                            <Lock size={11} />
                            Your connection is secure and encrypted.
                        </span>
                        <div className="support-links">
                            <a href="#help" className="support-link">Need Help?</a>
                            <span className="separator">•</span>
                            <a href="#contact" className="support-link">Contact Support</a>
                        </div>
                    </div> */}
        </div>
      </div>
    </div>
  );
};

/* ── App card (left panel) ────────────────────────────── */
const AppCard: React.FC<{
  icon: React.ReactNode;
  iconBg: string;
  name: string;
  status: string;
}> = ({ icon, iconBg, name, status }) => (
  <div className="lp-app-card" role="article" aria-label={name}>
    <div className="lp-app-icon-wrap" style={{ background: iconBg }}>
      {icon}
    </div>
    <span className="lp-app-name">{name}</span>
    <span className="lp-app-status">{status}</span>
  </div>
);

export default LoginPage;
